import { Coins, Layers, MapPin, ReceiptText, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import FxRate from "./fx-rate";
import Location from "./location";
import Categories from "./categories";
import Vendors from "./vendors";
import RecurringBill from "./recurring-bill";
import EmojiPicker from "emoji-picker-react";

export const card = (x = {}) => ({ background: C.cardBg, borderRadius: 14, border: `1px solid ${C.border}`, boxShadow: C.shadow, ...x });
export const pkr = n => n == null ? "—" : "₨ " + Number(n).toLocaleString("en-PK");
export const ord = d => { if (!d) return null; const s = ["th", "st", "nd", "rd"], v = d % 100; return d + (s[(v - 20) % 10] || s[v] || s[0]); };

export function EmojiField({
    value,
    onChange,
    onBlur,
    onKeyDown,
    C,
    style,
    position = "relative",
    islabel = true,
    isPickerLabel = true
}) {
    const [open, setOpen] = useState(false);
    const [pickerPos, setPickerPos] = useState({});
    const fieldRef = useRef(null);
    const pickerRef = useRef(null);

    const onEmojiClick = (emojiData) => {
        const emoji = emojiData.emoji;
        onChange?.(emoji);
        onBlur?.(emoji);
        setTimeout(() => {
            setOpen(false);
        }, 200);
    };

    const togglePicker = () => {
        if (!open && fieldRef.current) {
            const rect = fieldRef.current.getBoundingClientRect();

            const pickerWidth = 260;
            const pickerHeight = 320;

            const spaceBottom = window.innerHeight - rect.bottom;
            const spaceRight = window.innerWidth - rect.left;

            let top = rect.bottom + 8;
            let left = rect.left;

            if (spaceBottom < pickerHeight) {
                top = rect.top - pickerHeight - 8;
            }

            if (spaceRight < pickerWidth) {
                left = rect.right - pickerWidth;
            }

            setPickerPos({
                top,
                left,
                position: "fixed",
                zIndex: 9999,
            });
        }

        setOpen(!open);
    };

    useEffect(() => {
        const handleClick = (e) => {
            if (
                pickerRef.current &&
                !pickerRef.current.contains(e.target) &&
                fieldRef.current &&
                !fieldRef.current.contains(e.target)
            ) {
                setOpen(false);
                onBlur?.(value);
            }
        };

        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [value, onBlur]);

    return (
        <div style={{ position }} ref={fieldRef} onKeyDown={onKeyDown}>
            {islabel && (
                <div
                    style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C?.textMut,
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                        marginBottom: 5,
                    }}
                >
                    Emoji
                </div>
            )}

            <div
                onClick={togglePicker}
                style={{
                    ...style,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                }}
            >
                <span style={{ fontSize: 18 }}>
                    {value || ""}
                </span>

                {isPickerLabel && (
                    <span style={{ fontSize: 12, color: "#888" }}>Pick</span>
                )}
            </div>

            {open && (
                <div
                    ref={pickerRef}
                    style={{
                        ...pickerPos,
                        boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                        borderRadius: 10,
                        overflow: "hidden",
                        background: "#fff",
                    }}
                >
                    <EmojiPicker
                        onEmojiClick={onEmojiClick}
                        height={320}
                        width={260}
                        searchDisabled
                        previewConfig={{ showPreview: false }}
                    />
                </div>
            )}
        </div>
    );
}

// ── Section wrapper ───────────────────────────────────────────────────────────
export function Section({ id, icon, title, desc, children, Btn = null }) {
    return (
        <div
            id={id}
            className="bg-white/80 rounded-xl shadow-card p-7 scroll-mt-20"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-start justify-between mb-5 pb-[14px] border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-[11px] bg-gradient-to-br from-[#f0f4ff] to-[#e8edff] flex items-center justify-center text-[20px]">
                            {icon}
                        </div>

                        <div>
                            <div className="text-[15px] font-extrabold text-gray-900">
                                {title}
                            </div>
                            <div className="text-[12px] text-gray-500 mt-[2px]">{desc}</div>
                        </div>
                    </div>
                </div>

                {Btn && Btn}
            </div>

            {children}
        </div>
    );
}

// ── Inline editable field ─────────────────────────────────────────────────────
export function EditableCell({
    value,
    onSave,
    type = "text",
    placeholder = "",
    className = "",
}) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(value);
    const ref = useRef();

    const commit = (newVal = val) => {
        onSave(newVal);
        setEditing(false);
    };

    if (!editing)
        return (
            <span
                onClick={() => {
                    setVal(value);
                    setEditing(true);
                    setTimeout(() => ref.current?.focus(), 50);
                }}
                className={`cursor-text border-b border-dashed border-black/20 pb-[1px] ${className}`}
            >
                {value ? (
                    value
                ) : (
                    <span className="text-gray-400 italic">{placeholder}</span>
                )}
            </span>
        );

    return (
        type === "emoji" ? (
            <EmojiField
                value={val}
                onChange={setVal}
                onBlur={(emoji) => commit(emoji)}
                islabel={false}
                isPickerLabel={false}
                onKeyDown={(e) => {
                    if (e.key === "Enter") commit();
                    if (e.key === "Escape") setEditing(false);
                }}
            />
        ) : (
            <input
                ref={ref}
                type={type}
                value={val}
                onChange={(e) => setVal(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === "Enter") commit();
                    if (e.key === "Escape") setEditing(false);
                }}
                className={`px-[6px] py-[2px] rounded-md border border-[#4f6ef7] outline-none bg-white text-inherit focus:ring-0 ${type === "number" ? "w-[90px]" : "w-[140px]"} ${className}`}
            />
        )
    );
}

const C = {
    navBg: "#1e2128", pageBg: "#EDE8E1", cardBg: "#ffffff",
    accent: "#4f6ef7", green: "#16a34a", red: "#dc2626", amber: "#d97706",
    textPri: "#111827", textSec: "#6b7280", textMut: "#9ca3af",
    border: "rgba(0,0,0,0.05)", shadow: "0 1px 3px rgba(0,0,0,0.06),0 4px 16px rgba(0,0,0,0.08)",
};

// ── MAIN ─────────────────────────────────────────────────────────────────────
export default function ConfigAll() {
    const navList = [
        { name: "FX Rate", icon: Coins },
        { name: "Recurring Bills", icon: ReceiptText },
        { name: "Location", icon: MapPin },
        { name: "Categories", icon: Layers },
        { name: "Vendors", icon: Users },
    ]
    const [navActive, setNavActive] = useState("FX Rate");
    const [saved, setSaved] = useState(null); // {msg, type}
    const toast = (msg, type = "success") => { setSaved({msg, type}); setTimeout(() => setSaved(null), 2200); };
    const inpStyle = { padding: "7px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#f9f8f7", fontSize: 12, color: C.textPri, outline: "none", boxSizing: "border-box" };

    return (
        <div className="h-full bg-[#EDE8E1] flex flex-col overflow-auto">
            {/* Toast */}
            {saved && (
                <div className={`fixed bottom-7 left-1/2 -translate-x-1/2 z-[500] px-6 py-2.5 rounded-full bg-[#1e2128] text-[13px] font-semibold shadow-lg flex items-center gap-2 ${
                    saved.type === "error" ? "text-red-400" : 
                    saved.type === "info" ? "text-blue-400" : 
                    "text-green-400"
                }`}>
                    {saved.type === "error" ? "✗" : saved.type === "info" ? "ℹ" : "✓"} {saved.msg}
                </div>
            )}

            {/* Page */}
            <div className="w-full h-full overflow-hidden mx-auto flex flex-col gap-5 pt-5">
                {/* Header */}
                <div className="">
                    <h1 className="text-[22px] font-extrabold text-gray-900 tracking-tight">Configuration - {navActive}</h1>
                </div>

                <div className="w-full h-full overflow-hidden flex gap-5">
                    <div className="w-[420px] flex-shrink-0 h-full overflow-auto">
                        <div className="relative z-[2] flex items-start p-[9px_10px] rounded-[10px] bg-[#fff] w-full h-full overflow-auto shrink-0">
                            <ul className="flex w-full select-none flex-col gap-2.5">
                                {navList.map((nav, i) => {
                                    const Icon = nav.icon;
                                    return (
                                        <li
                                            key={i}
                                            className={`py-[14px] px-[16px] rounded-[10px] cursor-pointer text-sm flex items-center gap-1 font-semibold transition-all border ${navActive === nav.name ? "bg-[#0E121B] text-white border-[#0E121B]" : "bg-[#fff] text-gray-700 border-gray-200 hover:bg-gray-50"}`}
                                            onClick={() => setNavActive(nav.name)}
                                        >
                                            <span className="shrink-0">
                                                <Icon size={18} />
                                            </span>
                                            {nav.name}
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    </div>

                    <div className="flex flex-col gap-5 w-full h-full overflow-auto">
                        {navActive === "FX Rate" && (
                            <FxRate toast={(e) => { toast(e) }} inpStyle={inpStyle} C={C} />
                        )}

                        {navActive === "Location" && (
                            <Location C={C} inpStyle={inpStyle} toast={toast} />
                        )}

                        {navActive === "Categories" && (
                            <Categories C={C} inpStyle={inpStyle} toast={toast} />
                        )}

                        {navActive === "Vendors" && (
                            <Vendors C={C} inpStyle={inpStyle} toast={toast} />
                        )}

                        {navActive === "Recurring Bills" && (
                            <RecurringBill C={C} inpStyle={inpStyle} toast={toast} />
                        )}
                    </div>
                </div>


            </div>
        </div>
    );
}
